import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  IconButton,
  InputAdornment,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  useMediaQuery,
  Grid,
  Card,
  CardMedia,
} from "@mui/material";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Icon } from "../../icons";
import { COLORS, TYPOGRAPHY, FONT_FAMILY } from "../../constants/color";
import axios from "axios";
import { useAuth } from "../../context/authContext";
import { url } from "../../constants/url";
import AppToast from "../../components/AppToast";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format, startOfMonth, endOfMonth, parseISO } from "date-fns";

// Overall Progress Bar Chart Component
const OverallProgressBarChart = ({ progressItems, label }) => {
  const minBarHeight = 20;
  const maxBarHeight = 120;

  const calculateHeight = (value) => {
    const baseHeight = (value / 100) * maxBarHeight;
    return Math.max(baseHeight, minBarHeight);
  };

  const chartColors = [
    "#421A6E",
    "#652D90",
    "#8CD47E",
    "#FFB74D",
    "#4FC3F7",
    "#9575CD",
    "#F06292",
  ];

  return (
    <Box
      sx={{
        width: "100%",
        mt: 1,
        p: 2,
        border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
        borderRadius: "8px",
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: COLORS.PRIMARY,
          fontFamily: FONT_FAMILY,
          display: "block",
          mb: 2,
          fontSize: "14px",
          fontWeight: "bold",
        }}
      >
        {label}
      </Typography>

      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          height: maxBarHeight + 35,
          gap: 1.5,
          overflowX: "auto",
          pb: 1,
        }}
      >
        {/* Overall Progress Bar (100%) */}
        <Box
          sx={{
            minWidth: "50px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              mb: 0.5,
              color: COLORS.PRIMARY,
              fontFamily: FONT_FAMILY,
              fontSize: "10px",
              fontWeight: "bold",
            }}
          >
            Overall
          </Typography>
          <Box
            sx={{
              width: "35px",
              height: `${maxBarHeight}px`,
              backgroundColor: "#9C27B0",
              borderRadius: "4px 4px 0 0",
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "center",
              position: "relative",
            }}
          >
            <Typography
              sx={{
                color: "white",
                fontSize: "10px",
                fontWeight: "bold",
                mb: 0.5,
              }}
            >
              100%
            </Typography>
          </Box>
        </Box>

        {/* Dynamic Bars based on progressItems */}
        {(progressItems || []).map((item, index) => {
          const height = calculateHeight(parseFloat(item.percentage) || 0);
          const color = chartColors[index % chartColors.length];

          return (
            <Box
              key={index}
              sx={{
                minWidth: "50px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  mb: 0.5,
                  color: COLORS.PRIMARY,
                  fontFamily: FONT_FAMILY,
                  fontSize: "10px",
                  fontWeight: "bold",
                  textAlign: "center",
                  height: "32px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: "vertical",
                  width: "60px",
                  lineHeight: "1.1",
                }}
                title={item.category}
              >
                {item.category}
              </Typography>
              <Box
                sx={{
                  width: "35px",
                  height: `${height}px`,
                  backgroundColor: color,
                  borderRadius: "4px 4px 0 0",
                  display: "flex",
                  alignItems: "flex-end",
                  justifyContent: "center",
                  position: "relative",
                }}
              >
                <Typography
                  sx={{
                    color: "white",
                    fontSize: "9px",
                    fontWeight: "bold",
                    mb: 0.5,
                  }}
                >
                  {item.percentage}%
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* X-axis line */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          mt: 0.5,
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: "2px",
            backgroundColor: COLORS.LIGHT_SECONDARY,
          }}
        />
      </Box>
    </Box>
  );
};

// Bar Chart Component for Planned vs Achieved
const BarChart = ({ planValue, actualValue, label }) => {
  const minBarHeight = 20;
  const maxBarHeight = 120;

  const calculateHeight = (value) => {
    const baseHeight = (value / 100) * maxBarHeight;
    return Math.max(baseHeight, minBarHeight);
  };

  const planHeight = calculateHeight(planValue);
  const actualHeight = calculateHeight(actualValue);

  return (
    <Box
      sx={{
        width: "100%",
        mt: 1,
        p: 2,
        border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
        borderRadius: "8px",
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: COLORS.PRIMARY,
          fontFamily: FONT_FAMILY,
          display: "block",
          mb: 2,
          fontSize: "14px",
          fontWeight: "bold",
        }}
      >
        {label}
      </Typography>

      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          height: maxBarHeight + 30,
          gap: 3,
        }}
      >
        {/* Plan Bar */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              mb: 0.5,
              color: COLORS.PRIMARY,
              fontFamily: FONT_FAMILY,
              fontSize: "12px",
              fontWeight: "bold",
            }}
          >
            Plan
          </Typography>
          <Box
            sx={{
              width: "50px",
              height: `${planHeight}px`,
              backgroundColor: COLORS.PRIMARY,
              borderRadius: "4px 4px 0 0",
              transition: "height 0.5s ease-in-out",
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "center",
              position: "relative",
            }}
          >
            <Typography
              sx={{
                color: "white",
                fontSize: "12px",
                fontWeight: "bold",
                mb: 0.5,
              }}
            >
              {planValue}%
            </Typography>
          </Box>
        </Box>

        {/* Actual Bar */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              mb: 0.5,
              color: actualValue >= planValue ? "#4CAF50" : "#f44336",
              fontFamily: FONT_FAMILY,
              fontSize: "12px",
              fontWeight: "bold",
            }}
          >
            Actual
          </Typography>
          <Box
            sx={{
              width: "50px",
              height: `${actualHeight}px`,
              backgroundColor: actualValue >= planValue ? "#4CAF50" : "#f44336",
              borderRadius: "4px 4px 0 0",
              transition: "height 0.5s ease-in-out",
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "center",
              position: "relative",
            }}
          >
            <Typography
              sx={{
                color: "white",
                fontSize: "12px",
                fontWeight: "bold",
                mb: 0.5,
              }}
            >
              {actualValue}%
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* X-axis line */}
      <Box
        sx={{
          width: "100%",
          height: "2px",
          backgroundColor: COLORS.LIGHT_SECONDARY,
          mt: 0.5,
        }}
      />

      {/* Variance */}
      <Box
        sx={{
          mt: 2,
          p: 1,
          backgroundColor:
            actualValue >= planValue
              ? "rgba(76, 175, 80, 0.1)"
              : "rgba(244, 67, 54, 0.1)",
          borderRadius: "4px",
          textAlign: "center",
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: actualValue >= planValue ? "#4CAF50" : "#f44336",
            fontFamily: FONT_FAMILY,
            fontWeight: "bold",
            fontSize: "12px",
          }}
        >
          Variance: {actualValue - planValue}% (
          {actualValue >= planValue ? "Ahead" : "Behind"})
        </Typography>
      </Box>
    </Box>
  );
};

// Rich Text Display Component
const RichTextDisplay = ({ content, height = "auto" }) => {
  return (
    <Box
      sx={{
        border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
        borderRadius: "8px",
        minHeight: height,
        padding: "12px",
        fontFamily: FONT_FAMILY,
        fontSize: "14px",
        color: COLORS.PRIMARY,
        backgroundColor: "#fff",
        overflowY: "auto",
        "& p": {
          margin: "0 0 8px 0",
          color: COLORS.PRIMARY,
        },
        "& ul, & ol": {
          margin: "0 0 8px 0",
          paddingLeft: "20px",
          color: COLORS.PRIMARY,
        },
        "& li": {
          color: COLORS.PRIMARY,
        },
        "& strong, & b": {
          fontWeight: "bold",
        },
        "& em, & i": {
          fontStyle: "italic",
        },
        "& u": {
          textDecoration: "underline",
        },
      }}
      dangerouslySetInnerHTML={{ __html: content || "<p></p>" }}
    />
  );
};

export default function ViewMPR() {
  const { projectId } = useParams();
  const { token, userData } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery("(max-width: 768px)");

  // Get data from navigation state
  const { selectedMonth, monthStartDate, monthEndDate, project } =
    location.state || {};

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mprId, setMprId] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("");

  // Month date state
  const [monthStartDateState, setMonthStartDateState] = useState(
    monthStartDate ? new Date(monthStartDate) : null,
  );
  const [monthEndDateState, setMonthEndDateState] = useState(
    monthEndDate ? new Date(monthEndDate) : null,
  );
  const [selectedMonthState, setSelectedMonthState] = useState(
    selectedMonth ? new Date(selectedMonth) : null,
  );

  // Toast state
  const [toast, setToast] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
  });

  // MPR data state
  const [mprData, setMprData] = useState({
    executiveSummary: {
      overallStatus: "",
      keyAchievements: "",
      majorConcerns: "",
    },
    overallProgress: [],
    plannedVsAchieved: [],
    manpowerSummary: [],
    plantMachinery: [],
    materialManagement: [],
    qualityControl: {
      materialTestingSummary: [],
      inspectionAndNCRSummary: [],
    },
    safetyHSE: [],
    financialStatus: [],
    drawingsApprovals: {
      gfcDrawingReceived: [],
      pendingDrawing: [],
    },
    keyIssues: [],
    reports: [],
    decisionsRequired: [],
    rfiLogs: [],
    lookAheadPlan: [],
    photos: [],
  });

  const [uploadedReports, setUploadedReports] = useState([]);
  const [uploadedPhotos, setUploadedPhotos] = useState([]);
  const [downloading, setDownloading] = useState(false);

  // Format date functions
  const formatMonthForHeader = (date) => {
    if (!date) return "";
    return format(date, "MMMM yyyy");
  };

  // Fetch MPR data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!projectId || !monthStartDate || !monthEndDate) {
          setError(
            "Missing required information. Please go back and try again.",
          );
          setLoading(false);
          return;
        }

        const config = {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
          },
        };

        // Set project from navigation state
        if (project) {
          // Fetch companies for PMC dropdown
          try {
            const companiesResponse = await axios.get(
              `${url}/api/company/fetch`,
              config,
            );

            if (companiesResponse.data.data) {
              setCompanies(companiesResponse.data.data);
              if (companiesResponse.data.data.length > 0) {
                setSelectedCompany(companiesResponse.data.data[0]._id);
              }
            }
          } catch (companyErr) {
            console.error("Error fetching companies:", companyErr);
          }

          // Fetch MPR data by project and month dates
          if (!monthStartDateState || !monthEndDateState) return;

          const mprResponse = await axios.get(
            `${url}/api/mpr/fetch-by-project/${projectId}?monthStartDate=${format(monthStartDateState, "yyyy-MM-dd")}&monthEndDate=${format(monthEndDateState, "yyyy-MM-dd")}`,
            config,
          );

          if (mprResponse.data.mprs && mprResponse.data.mprs.length > 0) {
            const mpr = mprResponse.data.mprs[0];
            setMprId(mpr._id);

            // Set month dates
            if (mpr.monthStartDate) {
              const startDate = new Date(mpr.monthStartDate);
              const endDate = new Date(mpr.monthEndDate);
              setMonthStartDateState(startDate);
              setMonthEndDateState(endDate);
              setSelectedMonthState(startDate);
            }

            // Populate form with existing MPR data
            populateMPRData(mpr);
            showToast("info", "MPR Loaded", "MPR data loaded successfully.");
          } else {
            setError("No MPR found for the selected month.");
          }
        } else {
          setError("Project not found");
          showToast("error", "Error", "Project not found.");
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load MPR data. Please try again.");
        showToast("error", "Error", "Failed to load MPR data.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [projectId, monthStartDateState, monthEndDateState, project, token]);

  // Populate form data from existing MPR
  const populateMPRData = (mpr) => {
    const populatedData = {
      executiveSummary: {
        overallStatus: "",
        keyAchievements: "",
        majorConcerns: "",
      },
      overallProgress: [],
      plannedVsAchieved: [],
      manpowerSummary: [],
      plantMachinery: [],
      materialManagement: [],
      qualityControl: [],
      safetyHSE: [],
      financialStatus: [],
      drawingsApprovals: [],
      keyIssues: [],
      reports: [],
      decisionsRequired: [],
      rfiLogs: [],
      lookAheadPlan: [],
      photos: [],
    };

    // Convert executiveSummaries to separate fields
    if (mpr.executiveSummaries && mpr.executiveSummaries.length > 0) {
      const firstItem = mpr.executiveSummaries[0];
      populatedData.executiveSummary = {
        overallStatus: firstItem.overallStatus || "",
        keyAchievements: firstItem.keyAchievements || "",
        majorConcerns: firstItem.majorConcerns || "",
      };
    }

    // Convert overallProgress to array format
    if (mpr.overallProgress && mpr.overallProgress.length > 0) {
      populatedData.overallProgress = mpr.overallProgress.map((item) => ({
        _id: item._id,
        category: item.category || "",
        percentage: (item.percentage || 0).toString(),
      }));
    }

    // Map plannedVsAchieved
    if (mpr.plannedVsAchieved && mpr.plannedVsAchieved.length > 0) {
      populatedData.plannedVsAchieved = mpr.plannedVsAchieved.map((item) => ({
        _id: item._id,
        activity: item.activity || "",
        plannedDate: item.plannedDate
          ? format(parseISO(item.plannedDate), "dd/MM/yyyy")
          : "",
        actualDate: item.actualDate
          ? format(parseISO(item.actualDate), "dd/MM/yyyy")
          : "",
        variance: item.variance || "",
        remarkStatus: item.remarks || item.remarkStatus || "",
      }));
    }

    // Map manpowerSummaries
    if (mpr.manpowerSummaries && mpr.manpowerSummaries.length > 0) {
      populatedData.manpowerSummary = mpr.manpowerSummaries.map((item) => ({
        _id: item._id,
        category: item.category || "",
        averageManpower: item.averageManpower || 0,
        peakManpower: item.peakManpower || 0,
        remarks: item.remarks || "",
      }));
    }

    // Map other sections
    if (mpr.plantMachinery && mpr.plantMachinery.length > 0) {
      populatedData.plantMachinery = mpr.plantMachinery.map((item) => ({
        _id: item._id,
        equipmentDetails: item.equipmentName || "",
        capacity: item.capacity || "",
        quantity: item.quantity || 0,
        utilizationEfficiency: item.utilizationEfficiency || "",
        remarks: item.remarks || "",
      }));
    }

    // Map qualityAssurance
    if (mpr.qualityAssurance) {
      populatedData.qualityControl = {
        materialTestingSummary: (
          mpr.qualityAssurance.materialTestingSummary || []
        ).map((item) => ({
          ...item,
          testConductedDate: item.testConductedDate
            ? format(parseISO(item.testConductedDate), "dd/MM/yyyy")
            : "",
        })),
        inspectionAndNCRSummary: (
          mpr.qualityAssurance.inspectionAndNCRSummary || []
        ).map((item) => ({
          ...item,
          date: item.date ? format(parseISO(item.date), "dd/MM/yyyy") : "",
        })),
      };
    }

    // Map safetyHSE
    if (mpr.safetyHSE && mpr.safetyHSE.length > 0) {
      populatedData.safetyHSE = mpr.safetyHSE.map((item) => ({
        _id: item._id,
        date: item.date ? format(parseISO(item.date), "dd/MM/yyyy") : "",
        parameter: item.parameter || "",
        description: item.description || "",
        status: item.status || "Open",
        remarks: item.remarks || "",
      }));
    }

    // Map materialManagement
    if (mpr.materialManagement && mpr.materialManagement.length > 0) {
      populatedData.materialManagement = mpr.materialManagement.map((item) => ({
        _id: item._id,
        materialName: item.materialName || "",
        received: item.received || 0,
        consumed: item.consumed || 0,
        remarks: item.remarks || "",
      }));
    }

    // Map drawingsApprovals
    populatedData.drawingsApprovals = {
      gfcDrawingReceived: mpr.drawingsApprovals?.gfcDrawingReceived || [],
      pendingDrawing: mpr.drawingsApprovals?.pendingDrawing || [],
    };

    // Map keyIssues
    if (mpr.keyIssues && mpr.keyIssues.length > 0) {
      populatedData.keyIssues = mpr.keyIssues.map((item) => ({
        _id: item._id,
        issueDescription: item.issueDescription || "",
        delayReasons: item.delayReasons || "",
        mitigationMeasures: item.mitigationMeasures || "",
        impact: item.impact || "",
        remarks: item.remarks || "",
      }));
    }

    // Map clientDecisions
    if (mpr.clientDecisions && mpr.clientDecisions.length > 0) {
      populatedData.decisionsRequired = mpr.clientDecisions.map((item) => ({
        _id: item._id,
        decisionTopic:
          item.decisionTopic ||
          (item.approvals || item.designClarifications
            ? `${item.approvals || ""} | ${item.designClarifications || ""}`
            : ""),
        requiredByDate: item.requiredByDate
          ? format(parseISO(item.requiredByDate), "dd/MM/yyyy")
          : "",
        responsibility: item.responsibility || "",
        remarks: item.remarks || "",
      }));
    }

    // Map rfiLogs
    if (mpr.rfiLogs && mpr.rfiLogs.length > 0) {
      populatedData.rfiLogs = mpr.rfiLogs.map((item) => ({
        _id: item._id,
        rfiNo: item.rfiNo || "",
        description: item.description || "",
        issueDate: item.issueDate
          ? format(parseISO(item.issueDate), "dd/MM/yyyy")
          : "",
        status: item.status || "Open",
        expectedDate: item.expectedDate
          ? format(parseISO(item.expectedDate), "dd/MM/yyyy")
          : "",
        remarks: item.remark || item.remarks || "",
      }));
    }

    // Map financialStatus
    if (mpr.financialStatus && mpr.financialStatus.length > 0) {
      populatedData.financialStatus = mpr.financialStatus.map((item) => ({
        _id: item._id,
        description: item.description || "",
        amountSubmitted: (item.amountSubmitted || 0).toString(),
        amountCertified: (item.amountCertified || 0).toString(),
        paymentReceived: (item.paymentReceived || 0).toString(),
        remarks: item.remarks || "",
      }));
    }

    // Map lookAheadPlan
    if (mpr.lookAheadPlan && mpr.lookAheadPlan.length > 0) {
      populatedData.lookAheadPlan = mpr.lookAheadPlan.map((item) => ({
        _id: item._id,
        activity: item.activity || "",
        startDate: item.startDate
          ? format(new Date(item.startDate), "dd/MM/yyyy")
          : "",
        endDate: item.endDate
          ? format(new Date(item.endDate), "dd/MM/yyyy")
          : "",
        resourceRequirement: item.resourceRequirement || "",
        remarks: item.remarks || "",
      }));
    }

    // Map photographicDocumentation
    if (
      mpr.photographicDocumentation &&
      mpr.photographicDocumentation.length > 0
    ) {
      const photos = mpr.photographicDocumentation.map((photo) => {
        return {
          _id: photo._id,
          photoPath: photo.photoPath,
          description: photo.description || photo.photoTitle,
          uploadedAt: photo.dateTaken,
          name: photo.photoTitle || "Photo",
          imageUrl: `${url}/${photo.photoPath.replace(/\\/g, "/")}`,
        };
      });
      setUploadedPhotos(photos);
    }

    // Map reports
    if (mpr.reports && mpr.reports.length > 0) {
      const reports = mpr.reports.map((report) => ({
        _id: report._id,
        name: report.reportTitle,
        reportTitle: report.reportTitle,
        filePath: report.filePath,
        uploadDate: report.uploadDate,
        remarks: report.remarks,
      }));
      setUploadedReports(reports);
    }

    // Set all data at once
    setMprData(populatedData);
  };

  // Handle download report
  const handleDownloadReport = async () => {
    try {
      setDownloading(true);

      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
        responseType: "blob",
      };

      if (!mprId) {
        console.log("❌ No MPR ID available for download");
        showToast("error", "Error", "No MPR found to download.");
        setDownloading(false);
        return;
      }

      // Download the report
      const downloadResponse = await axios.get(
        `${url}/api/mpr/pdf/${mprId}`,
        config,
      );

      // Create blob and download
      const blob = new Blob([downloadResponse.data], {
        type: "application/pdf",
      });
      const urlBlob = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = urlBlob;

      // Generate filename
      const dateStr = monthStartDate
        ? format(monthStartDate, "yyyy-MM-dd")
        : new Date().toISOString().split("T")[0];
      const projectName =
        project?.projectName?.replace(/[^a-zA-Z0-9]/g, "_") || "MPR";
      link.download = `MPR_${projectName}_${dateStr}.pdf`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(urlBlob);

      showToast("success", "Success", "MPR report downloaded successfully!");
    } catch (err) {
      console.error("Error downloading MPR report:", err);
      const errorMessage =
        err.response?.data?.message ||
        "Failed to download MPR report. Please try again.";
      showToast("error", "Error", errorMessage);
    } finally {
      setDownloading(false);
    }
  };

  // Show toast function
  const showToast = (type, title, message) => {
    setToast({
      open: true,
      type,
      title,
      message,
    });
  };

  const closeToast = () => {
    setToast({ ...toast, open: false });
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={2}>
        <Alert severity="error">{error}</Alert>
        <Button onClick={() => navigate(-1)} sx={{ mt: 2 }}>
          Go Back
        </Button>
      </Box>
    );
  }

  if (!project) {
    return (
      <Box p={2}>
        <Alert severity="warning">Project not found</Alert>
        <Button onClick={() => navigate(-1)} sx={{ mt: 2 }}>
          Go Back
        </Button>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box p={2} sx={{ fontFamily: FONT_FAMILY }}>
        {/* ================= HEADER ================= */}
        <Box mb={2}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
          >
            <Box display="flex" alignItems="center" gap={1.5}>
              <Typography
                sx={{
                  ...TYPOGRAPHY.H2,
                  color: COLORS.PRIMARY,
                  fontFamily: FONT_FAMILY,
                }}
              >
                View Monthly Progress Report (MPR)
              </Typography>

              <Button
                onClick={handleDownloadReport}
                disabled={downloading || !mprId}
                startIcon={
                  downloading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <Icon name="download1" />
                  )
                }
                sx={{
                  backgroundColor: COLORS.SECONDARY,
                  color: "#fff",
                  textTransform: "none",
                  borderRadius: "7px",
                  px: 2,
                  height: 36,
                  "&:hover": {
                    backgroundColor: COLORS.PRIMARY,
                  },
                }}
              >
                {downloading ? "Downloading..." : "Download"}
              </Button>
            </Box>

            <IconButton size="small" onClick={() => navigate(-1)}>
              <Icon name="close3" color={COLORS.ERROR} />
            </IconButton>
          </Box>

          <Box display="flex" flexDirection="column" gap={0.4}>
            <Box
              display="flex"
              justifyContent="space-between"
              flexWrap={isMobile ? "wrap" : "nowrap"}
              gap={1}
            >
              <Typography
                sx={{ ...TYPOGRAPHY.BODY_STANDARD, fontFamily: FONT_FAMILY }}
              >
                Project Name:{" "}
                <span style={{ color: COLORS.SECONDARY }}>
                  {project.projectName}
                </span>
              </Typography>

              <Box
                display="flex"
                alignItems="center"
                gap={1}
                flexWrap={isMobile ? "wrap" : "nowrap"}
              >
                <Typography
                  sx={{ ...TYPOGRAPHY.BODY_STANDARD, fontFamily: FONT_FAMILY }}
                >
                  Month:
                </Typography>

                {selectedMonthState ? (
                  <Box display="flex" alignItems="center" gap={1}>
                    <TextField
                      size="small"
                      value={formatMonthForHeader(selectedMonthState)}
                      sx={{
                        width: isMobile ? "100%" : "200px",
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "6px",
                          height: "32px",
                          pr: 0.5,
                          fontFamily: FONT_FAMILY,
                          backgroundColor: "#f5f5f5",
                        },
                      }}
                      InputProps={{
                        readOnly: true,
                      }}
                    />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                      borderRadius: 1,
                      px: 1.2,
                      py: 0.3,
                      gap: 0.6,
                      fontSize: TYPOGRAPHY.BODY_SMALL.fontSize,
                      color: COLORS.PRIMARY,
                      minWidth: 110,
                      justifyContent: "space-between",
                      backgroundColor: "#f5f5f5",
                    }}
                  >
                    {selectedMonthState
                      ? formatMonthForHeader(selectedMonthState)
                      : "Select Month"}
                  </Box>
                )}
              </Box>
            </Box>

            <Box
              display="flex"
              justifyContent="space-between"
              flexWrap={isMobile ? "wrap" : "nowrap"}
              gap={1}
            >
              <Typography
                sx={{ ...TYPOGRAPHY.BODY_STANDARD, fontFamily: FONT_FAMILY }}
              >
                Address:{" "}
                <span style={{ color: COLORS.SECONDARY }}>
                  {project.siteAddress || "N/A"}
                </span>
              </Typography>

              <Typography
                sx={{ ...TYPOGRAPHY.BODY_STANDARD, fontFamily: FONT_FAMILY }}
              >
                PMC:{" "}
                <span style={{ color: COLORS.SECONDARY }}>
                  {companies.find((c) => c._id === selectedCompany)?.name ||
                    "N/A"}
                </span>
              </Typography>
            </Box>

            <Box
              display="flex"
              justifyContent="space-between"
              flexWrap={isMobile ? "wrap" : "nowrap"}
              gap={1}
            >
              <Typography
                sx={{ ...TYPOGRAPHY.BODY_STANDARD, fontFamily: FONT_FAMILY }}
              >
                Developer:{" "}
                <span style={{ color: COLORS.SECONDARY }}>
                  {project.developerName || "N/A"}
                </span>
              </Typography>

              <Typography
                sx={{ ...TYPOGRAPHY.BODY_STANDARD, fontFamily: FONT_FAMILY }}
              >
                Engineer:{" "}
                <span style={{ color: COLORS.SECONDARY }}>
                  {project.assignedEngineer?.name ||
                    project.assignedEngineerName ||
                    "Not Assigned"}
                </span>
              </Typography>
            </Box>
          </Box>

          {/* HEADER BOTTOM LINE */}
          <Box
            mt={1.5}
            height="1px"
            sx={{ backgroundColor: COLORS.LIGHT_SECONDARY }}
          />
        </Box>

        {/* ================= SECTIONS ================= */}
        {selectedMonthState && (
          <>
            {/* ================= 2. EXECUTIVE SUMMARY ================= */}
            <Box mt={4}>
              <Typography
                sx={{
                  ...TYPOGRAPHY.H3,
                  color: COLORS.PRIMARY,
                  fontFamily: FONT_FAMILY,
                  mb: 2,
                }}
              >
                2. Executive Summary
              </Typography>

              {/* Overall Project Status */}
              <Box mb={2.5}>
                <Typography
                  sx={{
                    ...TYPOGRAPHY.H4,
                    mb: 0.8,
                    fontFamily: FONT_FAMILY,
                    color: COLORS.PRIMARY,
                  }}
                >
                  Overall Project Status
                </Typography>
                <RichTextDisplay
                  content={mprData.executiveSummary.overallStatus}
                  height="150px"
                />
              </Box>

              {/* Key Achievements */}
              <Box mb={2.5}>
                <Typography
                  sx={{
                    ...TYPOGRAPHY.H4,
                    mb: 0.8,
                    fontFamily: FONT_FAMILY,
                    color: COLORS.PRIMARY,
                  }}
                >
                  Key Achievements
                </Typography>
                <RichTextDisplay
                  content={mprData.executiveSummary.keyAchievements}
                  height="150px"
                />
              </Box>

              {/* Major Concerns */}
              <Box mb={2.5}>
                <Typography
                  sx={{
                    ...TYPOGRAPHY.H4,
                    mb: 0.8,
                    fontFamily: FONT_FAMILY,
                    color: COLORS.PRIMARY,
                  }}
                >
                  Major Concerns
                </Typography>
                <RichTextDisplay
                  content={mprData.executiveSummary.majorConcerns}
                  height="150px"
                />
              </Box>
            </Box>

            {/* ================= 3. OVERALL PROGRESS SUMMARY ================= */}
            <Box mt={4}>
              <Typography
                sx={{
                  ...TYPOGRAPHY.H3,
                  color: COLORS.PRIMARY,
                  fontFamily: FONT_FAMILY,
                  mb: 2,
                }}
              >
                3. Overall Progress Summary
              </Typography>

              {/* Bar Chart */}
              {mprData.overallProgress.length > 0 && (
                <Box mb={3}>
                  <OverallProgressBarChart
                    progressItems={mprData.overallProgress}
                    label="Work Progress Distribution (100%)"
                  />
                </Box>
              )}

              {mprData.overallProgress.length === 0 && (
                <Box mb={1.5}>
                  <Box
                    sx={{
                      borderBottom: `2px solid ${COLORS.SECONDARY}`,
                      pb: 1,
                      mb: 1,
                      display: "flex",
                      px: 2,
                    }}
                  >
                    <Box flex={2}>
                      <Typography
                        sx={{
                          ...TYPOGRAPHY.H4,
                          color: COLORS.SECONDARY,
                          fontWeight: "bold",
                        }}
                      >
                        Category
                      </Typography>
                    </Box>
                    <Box flex={1}>
                      <Typography
                        sx={{
                          ...TYPOGRAPHY.H4,
                          color: COLORS.SECONDARY,
                          fontWeight: "bold",
                          textAlign: "center",
                        }}
                      >
                        Percentage (%)
                      </Typography>
                    </Box>
                  </Box>

                  {mprData.overallProgress.map((row, index) => (
                    <Box
                      key={index}
                      sx={{
                        px: 2,
                        py: 1.2,
                        display: "flex",
                        alignItems: "center",
                        borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                        gap: 2,
                      }}
                    >
                      <Box flex={2}>
                        <Typography
                          sx={{
                            fontFamily: FONT_FAMILY,
                            color: COLORS.SECONDARY,
                            fontWeight: 500,
                          }}
                        >
                          {row.category}
                        </Typography>
                      </Box>
                      <Box flex={1}>
                        <Typography
                          sx={{
                            textAlign: "center",
                            fontFamily: FONT_FAMILY,
                            color: COLORS.SECONDARY,
                          }}
                        >
                          {row.percentage}%
                        </Typography>
                      </Box>
                    </Box>
                  ))}

                  {/* Total Row */}
                  <Box
                    sx={{
                      backgroundColor: "rgba(66, 26, 110, 0.04)",
                      px: 2,
                      py: 1.5,
                      display: "flex",
                      alignItems: "center",
                      borderRadius: "4px",
                    }}
                  >
                    <Box flex={2}>
                      <Typography
                        sx={{ fontWeight: "bold", color: COLORS.SECONDARY }}
                      >
                        Total Progress
                      </Typography>
                    </Box>
                    <Box flex={1}>
                      <Typography
                        sx={{
                          textAlign: "center",
                          fontWeight: "bold",
                          color: COLORS.SUCCESS,
                          fontSize: "16px",
                        }}
                      >
                        {(mprData.overallProgress || [])
                          .reduce(
                            (sum, item) =>
                              sum + (parseFloat(item.percentage) || 0),
                            0,
                          )
                          .toFixed(1)}
                        %
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>

            {/* ================= 4. PLANNED VS ACHIEVED PROGRESS ================= */}
            <Box mt={4}>
              <Typography
                sx={{
                  ...TYPOGRAPHY.H3,
                  color: COLORS.PRIMARY,
                  fontFamily: FONT_FAMILY,
                  mb: 2,
                }}
              >
                4. Planned vs Achieved Progress (Monthly)
              </Typography>

              {/* Table - Read Only */}
              <Box mb={1.5}>
                {/* Table Header */}
                <Box
                  display="flex"
                  sx={{
                    borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                    py: 0.8,
                  }}
                >
                  <Box
                    flex={1.5}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Activity
                  </Box>
                  <Box
                    flex={1}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Planned Date
                  </Box>
                  <Box
                    flex={1}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Actual Date
                  </Box>
                  <Box
                    flex={1}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Variance
                  </Box>
                  <Box
                    flex={1.5}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Remark / Status
                  </Box>
                </Box>

                {/* Table Body */}
                {mprData.plannedVsAchieved.length > 0 ? (
                  mprData.plannedVsAchieved.map((row, index) => (
                    <Box
                      key={index}
                      display="flex"
                      sx={{
                        borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                        py: 1,
                        minHeight: "60px",
                      }}
                    >
                      <Box
                        flex={1.5}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.activity}
                      </Box>
                      <Box
                        flex={1}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.plannedDate}
                      </Box>
                      <Box
                        flex={1}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.actualDate}
                      </Box>
                      <Box
                        flex={1}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.variance}
                      </Box>
                      <Box
                        flex={1.5}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.remarkStatus}
                      </Box>
                    </Box>
                  ))
                ) : (
                  <Box
                    display="flex"
                    sx={{
                      borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                      py: 2,
                      justifyContent: "center",
                      minHeight: "60px",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: FONT_FAMILY,
                        color: COLORS.LIGHT_SECONDARY,
                        fontStyle: "italic",
                      }}
                    >
                      No entries available.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {/* ================= 6. MANPOWER SUMMARY ================= */}
            <Box mt={4}>
              <Typography
                sx={{
                  ...TYPOGRAPHY.H3,
                  color: COLORS.PRIMARY,
                  fontFamily: FONT_FAMILY,
                  mb: 2,
                }}
              >
                6. Manpower Summary
              </Typography>

              {/* Table - Read Only */}
              <Box mb={1.5}>
                {/* Table Header */}
                <Box
                  display="flex"
                  sx={{
                    borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                    py: 0.8,
                  }}
                >
                  <Box
                    flex={1.5}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Category / Labour Type
                  </Box>
                  <Box
                    flex={1}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Average Manpower
                  </Box>
                  <Box
                    flex={1}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Peak Manpower
                  </Box>
                  <Box
                    flex={1.5}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Remarks
                  </Box>
                </Box>

                {/* Table Body */}
                {mprData.manpowerSummary.length > 0 ? (
                  mprData.manpowerSummary.map((row, index) => (
                    <Box
                      key={index}
                      display="flex"
                      sx={{
                        borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                        py: 1,
                        minHeight: "60px",
                      }}
                    >
                      <Box
                        flex={1.5}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.category}
                      </Box>
                      <Box
                        flex={1}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.averageManpower}
                      </Box>
                      <Box
                        flex={1}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.peakManpower}
                      </Box>
                      <Box
                        flex={1.5}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.remarks}
                      </Box>
                    </Box>
                  ))
                ) : (
                  <Box
                    display="flex"
                    sx={{
                      borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                      py: 2,
                      justifyContent: "center",
                      minHeight: "60px",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: FONT_FAMILY,
                        color: COLORS.LIGHT_SECONDARY,
                        fontStyle: "italic",
                      }}
                    >
                      No entries available.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {/* ================= 6. MACHINERY / EQUIPMENT DEPLOYMENT ================= */}
            <Box mt={4}>
              <Typography
                sx={{
                  ...TYPOGRAPHY.H3,
                  color: COLORS.PRIMARY,
                  fontFamily: FONT_FAMILY,
                  mb: 2,
                }}
              >
                6. Machinery / Equipment Deployment
              </Typography>

              {/* Table - Read Only */}
              <Box mb={1.5}>
                {/* Table Header */}
                <Box
                  display="flex"
                  sx={{
                    borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                    py: 0.8,
                  }}
                >
                  <Box
                    flex={1.5}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Equipment Details
                  </Box>
                  <Box
                    flex={0.8}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Capacity
                  </Box>
                  <Box
                    flex={0.6}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Quantity
                  </Box>
                  <Box
                    flex={0.8}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Utilization Efficiency
                  </Box>
                  <Box
                    flex={1.2}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Remarks
                  </Box>
                </Box>

                {/* Table Body */}
                {mprData.plantMachinery.length > 0 ? (
                  mprData.plantMachinery.map((row, index) => (
                    <Box
                      key={index}
                      display="flex"
                      sx={{
                        borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                        py: 1,
                        minHeight: "60px",
                      }}
                    >
                      <Box
                        flex={1.5}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.equipmentDetails}
                      </Box>
                      <Box
                        flex={0.8}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.capacity}
                      </Box>
                      <Box
                        flex={0.6}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.quantity}
                      </Box>
                      <Box
                        flex={0.8}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.utilizationEfficiency}
                      </Box>
                      <Box
                        flex={1.2}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.remarks}
                      </Box>
                    </Box>
                  ))
                ) : (
                  <Box
                    display="flex"
                    sx={{
                      borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                      py: 2,
                      justifyContent: "center",
                      minHeight: "60px",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: FONT_FAMILY,
                        color: COLORS.LIGHT_SECONDARY,
                        fontStyle: "italic",
                      }}
                    >
                      No entries available.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {/* ================= 8. MATERIAL MANAGEMENT ================= */}
            <Box mt={4}>
              <Typography
                sx={{
                  ...TYPOGRAPHY.H3,
                  color: COLORS.PRIMARY,
                  fontFamily: FONT_FAMILY,
                  mb: 2,
                }}
              >
                7. Material Management
              </Typography>

              {/* Table - Read Only */}
              <Box mb={1.5}>
                {/* Table Header */}
                <Box
                  display="flex"
                  sx={{
                    borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                    py: 0.8,
                  }}
                >
                  <Box
                    flex={1.5}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Material Name
                  </Box>
                  <Box
                    flex={0.8}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Recieve
                  </Box>
                  <Box
                    flex={0.8}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Consumed
                  </Box>
                  <Box
                    flex={1.2}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Remarks
                  </Box>
                </Box>

                {/* Table Body */}
                {mprData.materialManagement.length > 0 ? (
                  mprData.materialManagement.map((row, index) => (
                    <Box
                      key={index}
                      display="flex"
                      sx={{
                        borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                        py: 1,
                        minHeight: "60px",
                      }}
                    >
                      <Box
                        flex={1.5}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.materialName}
                      </Box>
                      <Box
                        flex={0.8}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.received}
                      </Box>
                      <Box
                        flex={0.8}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.consumed}
                      </Box>
                      <Box
                        flex={1.2}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.remarks}
                      </Box>
                    </Box>
                  ))
                ) : (
                  <Box
                    display="flex"
                    sx={{
                      borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                      py: 2,
                      justifyContent: "center",
                      minHeight: "60px",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: FONT_FAMILY,
                        color: COLORS.LIGHT_SECONDARY,
                        fontStyle: "italic",
                      }}
                    >
                      No entries available.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {/* ================= 9. QUALITY ASSURANCE & QUALITY CONTROL ================= */}
            <Box mt={4}>
              <Typography
                sx={{
                  ...TYPOGRAPHY.H3,
                  color: COLORS.PRIMARY,
                  fontFamily: FONT_FAMILY,
                  mb: 2,
                }}
              >
                9. Quality Assurance & Quality Control
              </Typography>

              {/* 9.1 Material Testing Summary */}
              <Typography
                sx={{
                  ...TYPOGRAPHY.H4,
                  color: COLORS.SECONDARY,
                  fontFamily: FONT_FAMILY,
                  mb: 2,
                  mt: 3,
                }}
              >
                9.1 Material Testing Summary
              </Typography>

              <Box mb={1.5}>
                {/* Table Header */}
                <Box
                  display="flex"
                  sx={{
                    borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                    py: 0.8,
                  }}
                >
                  <Box
                    flex={1.5}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Test Description
                  </Box>
                  <Box
                    flex={1.2}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Date
                  </Box>
                  <Box
                    flex={1}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Report ID
                  </Box>
                  <Box
                    flex={1}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Result
                  </Box>
                  <Box
                    flex={1}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Status
                  </Box>
                </Box>

                {/* Table Body */}
                {mprData.qualityControl.materialTestingSummary.length > 0 ? (
                  mprData.qualityControl.materialTestingSummary.map(
                    (row, index) => (
                      <Box
                        key={index}
                        display="flex"
                        sx={{
                          borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                          py: 1,
                          minHeight: "60px",
                        }}
                      >
                        <Box
                          flex={1.5}
                          sx={{
                            ...TYPOGRAPHY.BODY_LARGE,
                            fontFamily: FONT_FAMILY,
                            color: COLORS.SECONDARY,
                            display: "flex",
                            alignItems: "center",
                            px: 1,
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                            whiteSpace: "normal",
                            minHeight: "40px",
                          }}
                        >
                          {row.testDescription}
                        </Box>
                        <Box
                          flex={1.2}
                          sx={{
                            ...TYPOGRAPHY.BODY_LARGE,
                            fontFamily: FONT_FAMILY,
                            color: COLORS.SECONDARY,
                            display: "flex",
                            alignItems: "center",
                            px: 1,
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                            whiteSpace: "normal",
                            minHeight: "40px",
                          }}
                        >
                          {row.testConductedDate}
                        </Box>
                        <Box
                          flex={1}
                          sx={{
                            ...TYPOGRAPHY.BODY_LARGE,
                            fontFamily: FONT_FAMILY,
                            color: COLORS.SECONDARY,
                            display: "flex",
                            alignItems: "center",
                            px: 1,
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                            whiteSpace: "normal",
                            minHeight: "40px",
                          }}
                        >
                          {row.reportId}
                        </Box>
                        <Box
                          flex={1}
                          sx={{
                            ...TYPOGRAPHY.BODY_LARGE,
                            fontFamily: FONT_FAMILY,
                            color: COLORS.SECONDARY,
                            display: "flex",
                            alignItems: "center",
                            px: 1,
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                            whiteSpace: "normal",
                            minHeight: "40px",
                          }}
                        >
                          {row.result}
                        </Box>
                        <Box
                          flex={1}
                          sx={{
                            ...TYPOGRAPHY.BODY_LARGE,
                            fontFamily: FONT_FAMILY,
                            color: COLORS.SECONDARY,
                            display: "flex",
                            alignItems: "center",
                            px: 1,
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                            whiteSpace: "normal",
                            minHeight: "40px",
                          }}
                        >
                          {row.status}
                        </Box>
                      </Box>
                    ),
                  )
                ) : (
                  <Box
                    display="flex"
                    sx={{
                      borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                      py: 2,
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: FONT_FAMILY,
                        color: COLORS.LIGHT_SECONDARY,
                        fontStyle: "italic",
                      }}
                    >
                      No entries available.
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* 9.2 Inspection & NCR Summary */}
              <Typography
                sx={{
                  ...TYPOGRAPHY.H4,
                  color: COLORS.SECONDARY,
                  fontFamily: FONT_FAMILY,
                  mb: 2,
                  mt: 4,
                }}
              >
                9.2 Inspection & NCR Summary
              </Typography>

              <Box mb={1.5}>
                <Box
                  display="flex"
                  sx={{
                    borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                    py: 0.8,
                  }}
                >
                  <Box
                    flex={1}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    NCR No.
                  </Box>
                  <Box
                    flex={1}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Date
                  </Box>
                  <Box
                    flex={1}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Category
                  </Box>
                  <Box
                    flex={2}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Description
                  </Box>
                  <Box
                    flex={1}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Status
                  </Box>
                </Box>

                {mprData.qualityControl.inspectionAndNCRSummary.length > 0 ? (
                  mprData.qualityControl.inspectionAndNCRSummary.map(
                    (row, index) => (
                      <Box
                        key={index}
                        display="flex"
                        sx={{
                          borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                          py: 1,
                          minHeight: "60px",
                        }}
                      >
                        <Box
                          flex={1}
                          sx={{
                            ...TYPOGRAPHY.BODY_LARGE,
                            fontFamily: FONT_FAMILY,
                            color: COLORS.SECONDARY,
                            display: "flex",
                            alignItems: "center",
                            px: 1,
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                            whiteSpace: "normal",
                            minHeight: "40px",
                          }}
                        >
                          {row.ncrNo}
                        </Box>
                        <Box
                          flex={1}
                          sx={{
                            ...TYPOGRAPHY.BODY_LARGE,
                            fontFamily: FONT_FAMILY,
                            color: COLORS.SECONDARY,
                            display: "flex",
                            alignItems: "center",
                            px: 1,
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                            whiteSpace: "normal",
                            minHeight: "40px",
                          }}
                        >
                          {row.date}
                        </Box>
                        <Box
                          flex={1}
                          sx={{
                            ...TYPOGRAPHY.BODY_LARGE,
                            fontFamily: FONT_FAMILY,
                            color: COLORS.SECONDARY,
                            display: "flex",
                            alignItems: "center",
                            px: 1,
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                            whiteSpace: "normal",
                            minHeight: "40px",
                          }}
                        >
                          {row.category}
                        </Box>
                        <Box
                          flex={2}
                          sx={{
                            ...TYPOGRAPHY.BODY_LARGE,
                            fontFamily: FONT_FAMILY,
                            color: COLORS.SECONDARY,
                            display: "flex",
                            alignItems: "center",
                            px: 1,
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                            whiteSpace: "normal",
                            minHeight: "40px",
                          }}
                        >
                          {row.description}
                        </Box>
                        <Box
                          flex={1}
                          sx={{
                            ...TYPOGRAPHY.BODY_LARGE,
                            fontFamily: FONT_FAMILY,
                            color: COLORS.SECONDARY,
                            display: "flex",
                            alignItems: "center",
                            px: 1,
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                            whiteSpace: "normal",
                            minHeight: "40px",
                          }}
                        >
                          {row.status}
                        </Box>
                      </Box>
                    ),
                  )
                ) : (
                  <Box
                    display="flex"
                    sx={{
                      borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                      py: 2,
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: FONT_FAMILY,
                        color: COLORS.LIGHT_SECONDARY,
                        fontStyle: "italic",
                      }}
                    >
                      No entries available.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {/* ================= 10. SAFETY, HEALTH & ENVIRONMENT (SHE) ================= */}
            <Box mt={4}>
              <Typography
                sx={{
                  ...TYPOGRAPHY.H3,
                  color: COLORS.PRIMARY,
                  fontFamily: FONT_FAMILY,
                  mb: 2,
                }}
              >
                10. Safety, Health & Environment (SHE)
              </Typography>

              <Box mb={1.5}>
                {/* Table Header */}
                <Box
                  display="flex"
                  sx={{
                    borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                    py: 0.8,
                  }}
                >
                  <Box
                    flex={1}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Date
                  </Box>
                  <Box
                    flex={1.5}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Parameter
                  </Box>
                  <Box
                    flex={2}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Description
                  </Box>
                  <Box
                    flex={1}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Status
                  </Box>
                  <Box
                    flex={1.5}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Remark
                  </Box>
                </Box>

                {/* Table Body */}
                {mprData.safetyHSE.length > 0 ? (
                  mprData.safetyHSE.map((row, index) => (
                    <Box
                      key={index}
                      display="flex"
                      sx={{
                        borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                        py: 1,
                        minHeight: "60px",
                      }}
                    >
                      <Box
                        flex={1}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.date}
                      </Box>
                      <Box
                        flex={1.5}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.parameter}
                      </Box>
                      <Box
                        flex={2}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.description}
                      </Box>
                      <Box
                        flex={1}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color:
                            row.status === "Closed"
                              ? COLORS.SUCCESS
                              : COLORS.ERROR,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.status}
                      </Box>
                      <Box
                        flex={1.5}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.remarks}
                      </Box>
                    </Box>
                  ))
                ) : (
                  <Box
                    display="flex"
                    sx={{
                      borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                      py: 2,
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: FONT_FAMILY,
                        color: COLORS.LIGHT_SECONDARY,
                        fontStyle: "italic",
                      }}
                    >
                      No entries available.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {/* ================= 11. FINANCIAL STATUS ================= */}
            <Box mt={4}>
              <Typography
                sx={{
                  ...TYPOGRAPHY.H3,
                  color: COLORS.PRIMARY,
                  fontFamily: FONT_FAMILY,
                  mb: 2,
                }}
              >
                11. Financial Status
              </Typography>

              {/* Table - Read Only */}
              <Box mb={1.5}>
                {/* Table Header */}
                <Box
                  display="flex"
                  sx={{
                    borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                    py: 0.8,
                  }}
                >
                  <Box
                    flex={1.5}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Description
                  </Box>
                  <Box
                    flex={1}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Amount Submitted
                  </Box>
                  <Box
                    flex={1}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Amount Certified
                  </Box>
                  <Box
                    flex={1}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                      mr: 2,
                    }}
                  >
                    Payment Received
                  </Box>
                  <Box
                    flex={1.5}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Remarks
                  </Box>
                </Box>

                {/* Table Body */}
                {mprData.financialStatus.length > 0 ? (
                  mprData.financialStatus.map((row, index) => (
                    <Box
                      key={index}
                      display="flex"
                      sx={{
                        borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                        py: 1,
                        minHeight: "60px",
                      }}
                    >
                      <Box
                        flex={1.5}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.description}
                      </Box>
                      <Box
                        flex={1}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.amountSubmitted}
                      </Box>
                      <Box
                        flex={1}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.amountCertified}
                      </Box>
                      <Box
                        flex={1}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                          mr: 2,
                        }}
                      >
                        {row.paymentReceived}
                      </Box>
                      <Box
                        flex={1.5}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.remarks}
                      </Box>
                    </Box>
                  ))
                ) : (
                  <Box
                    display="flex"
                    sx={{
                      borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                      py: 2,
                      justifyContent: "center",
                      minHeight: "60px",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: FONT_FAMILY,
                        color: COLORS.LIGHT_SECONDARY,
                        fontStyle: "italic",
                      }}
                    >
                      No entries available.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {/* ================= 12. DRAWINGS & APPROVALS STATUS ================= */}
            <Box mt={4}>
              <Typography
                sx={{
                  ...TYPOGRAPHY.H3,
                  color: COLORS.PRIMARY,
                  fontFamily: FONT_FAMILY,
                  mb: 2,
                }}
              >
                12. Drawings & Approvals Status
              </Typography>

              {/* GFC Drawings Received */}
              <Box mb={1.5}>
                {/* Table Header */}
                <Box
                  display="flex"
                  sx={{
                    borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                    py: 0.8,
                  }}
                >
                  <Box
                    flex={1}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Category
                  </Box>
                  <Box
                    flex={1}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Drawing Type
                  </Box>
                  <Box
                    flex={1}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Drawing No.
                  </Box>
                  <Box
                    flex={1}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Revision
                  </Box>
                  <Box
                    flex={1}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Received Date
                  </Box>
                  <Box
                    flex={1.5}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Remark / Status
                  </Box>
                </Box>

                {/* Table Body */}
                {mprData.drawingsApprovals?.gfcDrawingReceived?.length > 0 ? (
                  mprData.drawingsApprovals.gfcDrawingReceived.map(
                    (row, index) => (
                      <Box
                        key={index}
                        display="flex"
                        sx={{
                          borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                          py: 1,
                          minHeight: "60px",
                        }}
                      >
                        <Box
                          flex={1}
                          sx={{
                            ...TYPOGRAPHY.BODY_LARGE,
                            fontFamily: FONT_FAMILY,
                            color: COLORS.SECONDARY,
                            display: "flex",
                            alignItems: "center",
                            px: 1,
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                            whiteSpace: "normal",
                            minHeight: "40px",
                          }}
                        >
                          {row.category}
                        </Box>
                        <Box
                          flex={1}
                          sx={{
                            ...TYPOGRAPHY.BODY_LARGE,
                            fontFamily: FONT_FAMILY,
                            color: COLORS.SECONDARY,
                            display: "flex",
                            alignItems: "center",
                            px: 1,
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                            whiteSpace: "normal",
                            minHeight: "40px",
                          }}
                        >
                          {row.drawingType}
                        </Box>
                        <Box
                          flex={1}
                          sx={{
                            ...TYPOGRAPHY.BODY_LARGE,
                            fontFamily: FONT_FAMILY,
                            color: COLORS.SECONDARY,
                            display: "flex",
                            alignItems: "center",
                            px: 1,
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                            whiteSpace: "normal",
                            minHeight: "40px",
                          }}
                        >
                          {row.drawingNo}
                        </Box>
                        <Box
                          flex={1}
                          sx={{
                            ...TYPOGRAPHY.BODY_LARGE,
                            fontFamily: FONT_FAMILY,
                            color: COLORS.SECONDARY,
                            display: "flex",
                            alignItems: "center",
                            px: 1,
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                            whiteSpace: "normal",
                            minHeight: "40px",
                          }}
                        >
                          {row.revision}
                        </Box>
                        <Box
                          flex={1}
                          sx={{
                            ...TYPOGRAPHY.BODY_LARGE,
                            fontFamily: FONT_FAMILY,
                            color: COLORS.SECONDARY,
                            display: "flex",
                            alignItems: "center",
                            px: 1,
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                            whiteSpace: "normal",
                            minHeight: "40px",
                          }}
                        >
                          {row.receivedDate
                            ? new Date(row.receivedDate).toLocaleDateString()
                            : ""}
                        </Box>
                        <Box
                          flex={1.5}
                          sx={{
                            ...TYPOGRAPHY.BODY_LARGE,
                            fontFamily: FONT_FAMILY,
                            color: COLORS.SECONDARY,
                            display: "flex",
                            alignItems: "center",
                            px: 1,
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                            whiteSpace: "normal",
                            minHeight: "40px",
                          }}
                        >
                          {row.remark || row.status}
                        </Box>
                      </Box>
                    ),
                  )
                ) : (
                  <Box
                    display="flex"
                    sx={{
                      borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                      py: 2,
                      justifyContent: "center",
                      minHeight: "60px",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: FONT_FAMILY,
                        color: COLORS.LIGHT_SECONDARY,
                        fontStyle: "italic",
                      }}
                    >
                      No GFC drawings received.
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* 12.2 Pending Drawings */}
              <Box mb={3}>
                <Typography
                  sx={{
                    ...TYPOGRAPHY.H4,
                    color: COLORS.PRIMARY,
                    fontFamily: FONT_FAMILY,
                    mb: 1.5,
                  }}
                >
                  12.2 Pending Drawings
                </Typography>

                {/* Pending Table Header */}
                <Box mb={1}>
                  <Box
                    display="flex"
                    sx={{
                      borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                      py: 0.8,
                    }}
                  >
                    <Box
                      flex={1}
                      sx={{
                        ...TYPOGRAPHY.BODY_LARGE,
                        fontFamily: FONT_FAMILY,
                        color: COLORS.TEXT,
                        fontWeight: "bold",
                        px: 1,
                      }}
                    >
                      Category
                    </Box>
                    <Box
                      flex={1}
                      sx={{
                        ...TYPOGRAPHY.BODY_LARGE,
                        fontFamily: FONT_FAMILY,
                        color: COLORS.TEXT,
                        fontWeight: "bold",
                        px: 1,
                      }}
                    >
                      Drawing Type
                    </Box>
                    <Box
                      flex={1}
                      sx={{
                        ...TYPOGRAPHY.BODY_LARGE,
                        fontFamily: FONT_FAMILY,
                        color: COLORS.TEXT,
                        fontWeight: "bold",
                        px: 1,
                      }}
                    >
                      Expected Date
                    </Box>
                    <Box
                      flex={1}
                      sx={{
                        ...TYPOGRAPHY.BODY_LARGE,
                        fontFamily: FONT_FAMILY,
                        color: COLORS.TEXT,
                        fontWeight: "bold",
                        px: 1,
                      }}
                    >
                      Current Status
                    </Box>
                    <Box
                      flex={1.5}
                      sx={{
                        ...TYPOGRAPHY.BODY_LARGE,
                        fontFamily: FONT_FAMILY,
                        color: COLORS.TEXT,
                        fontWeight: "bold",
                        px: 1,
                      }}
                    >
                      Remark
                    </Box>
                  </Box>
                </Box>

                {/* Pending Table Body */}
                {mprData.drawingsApprovals?.pendingDrawing?.length > 0 ? (
                  mprData.drawingsApprovals.pendingDrawing.map((row, index) => (
                    <Box
                      key={index}
                      display="flex"
                      sx={{
                        borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                        py: 1,
                        minHeight: "60px",
                      }}
                    >
                      <Box
                        flex={1}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.category}
                      </Box>
                      <Box
                        flex={1}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.drawingType}
                      </Box>
                      <Box
                        flex={1}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.expectedDate
                          ? new Date(row.expectedDate).toLocaleDateString()
                          : ""}
                      </Box>
                      <Box
                        flex={1}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.currentStatus}
                      </Box>
                      <Box
                        flex={1.5}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.remark}
                      </Box>
                    </Box>
                  ))
                ) : (
                  <Box
                    display="flex"
                    sx={{
                      borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                      py: 2,
                      justifyContent: "center",
                      minHeight: "60px",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: FONT_FAMILY,
                        color: COLORS.LIGHT_SECONDARY,
                        fontStyle: "italic",
                      }}
                    >
                      No pending drawings.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {/* ================= 13. KEY ISSUES & RISK AREAS ================= */}
            <Box mt={4}>
              <Typography
                sx={{
                  ...TYPOGRAPHY.H3,
                  color: COLORS.PRIMARY,
                  fontFamily: FONT_FAMILY,
                  mb: 2,
                }}
              >
                13. Key Issues & Risk Areas
              </Typography>

              {/* Table - Read Only */}
              <Box mb={1.5}>
                {/* Table Header */}
                <Box
                  display="flex"
                  sx={{
                    borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                    py: 0.8,
                  }}
                >
                  <Box
                    flex={1.5}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                      px: 1,
                    }}
                  >
                    Issues Description
                  </Box>
                  <Box
                    flex={1.5}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                      px: 1,
                    }}
                  >
                    Mitigation measures
                  </Box>
                  <Box
                    flex={1}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                      px: 1,
                    }}
                  >
                    Impact level
                  </Box>
                  <Box
                    flex={1}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                      px: 1,
                    }}
                  >
                    Remarks
                  </Box>
                </Box>

                {/* Table Body */}
                {mprData.keyIssues.length > 0 ? (
                  mprData.keyIssues.map((row, index) => (
                    <Box
                      key={index}
                      display="flex"
                      sx={{
                        borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                        py: 1,
                        minHeight: "60px",
                      }}
                    >
                      <Box
                        flex={1.5}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.issueDescription}
                      </Box>
                      <Box
                        flex={1.5}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.mitigationMeasures}
                      </Box>
                      <Box
                        flex={1}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.impact}
                      </Box>
                      <Box
                        flex={1}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.remarks}
                      </Box>
                    </Box>
                  ))
                ) : (
                  <Box
                    display="flex"
                    sx={{
                      borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                      py: 2,
                      justifyContent: "center",
                      minHeight: "60px",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: FONT_FAMILY,
                        color: COLORS.LIGHT_SECONDARY,
                        fontStyle: "italic",
                      }}
                    >
                      No entries available.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {/* ================= 14. REPORTS ================= */}
            <Box mt={4}>
              <Typography
                sx={{
                  ...TYPOGRAPHY.H3,
                  fontFamily: FONT_FAMILY,
                  color: COLORS.PRIMARY,
                  mb: 2,
                }}
              >
                14. Reports
              </Typography>

              {uploadedReports.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  {uploadedReports.map((file, index) => (
                    <Box
                      key={index}
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{
                        border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                        borderRadius: "8px",
                        px: 2,
                        py: 1,
                        mb: 1,
                      }}
                    >
                      <Typography
                        sx={{ fontFamily: FONT_FAMILY, color: COLORS.PRIMARY }}
                      >
                        {file.name}
                      </Typography>
                      <Box display="flex" gap={1}>
                        <IconButton
                          onClick={() => {
                            if (file.filePath) {
                              const fileUrl = `${url}/${file.filePath.replace(/\\/g, "/").replace(/^\//, "")}`;
                              window.open(fileUrl, "_blank");
                            }
                          }}
                        >
                          <Icon name="Action1" />
                        </IconButton>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
              {uploadedReports.length === 0 && (
                <Typography
                  sx={{
                    fontFamily: FONT_FAMILY,
                    color: COLORS.LIGHT_SECONDARY,
                    fontStyle: "italic",
                    mb: 2,
                  }}
                >
                  No reports uploaded.
                </Typography>
              )}
            </Box>

            {/* ================= 15. DECISIONS REQUIRED ================= */}
            <Box mt={4}>
              <Typography
                sx={{
                  ...TYPOGRAPHY.H3,
                  color: COLORS.PRIMARY,
                  fontFamily: FONT_FAMILY,
                  mb: 2,
                }}
              >
                15. Decisions Required
              </Typography>

              {/* Table - Read Only */}
              <Box mb={1.5}>
                {/* Table Header */}
                <Box
                  display="flex"
                  sx={{
                    borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                    py: 0.8,
                  }}
                >
                  <Box
                    flex={1.5}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                      px: 1,
                    }}
                  >
                    Decision Topic
                  </Box>
                  <Box
                    flex={1}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                      px: 1,
                    }}
                  >
                    Required By Date
                  </Box>
                  <Box
                    flex={1}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                      px: 1,
                    }}
                  >
                    Responsibility
                  </Box>
                  <Box
                    flex={1}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                      px: 1,
                    }}
                  >
                    Remark
                  </Box>
                </Box>

                {/* Table Body */}
                {mprData.decisionsRequired.length > 0 ? (
                  mprData.decisionsRequired.map((row, index) => (
                    <Box
                      key={index}
                      display="flex"
                      sx={{
                        borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                        py: 1,
                        minHeight: "60px",
                      }}
                    >
                      <Box
                        flex={1.5}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.decisionTopic}
                      </Box>
                      <Box
                        flex={1}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.requiredByDate}
                      </Box>
                      <Box
                        flex={1}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.responsibility}
                      </Box>
                      <Box
                        flex={1}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.remarks}
                      </Box>
                    </Box>
                  ))
                ) : (
                  <Box
                    display="flex"
                    sx={{
                      borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                      py: 2,
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: FONT_FAMILY,
                        color: COLORS.LIGHT_SECONDARY,
                        fontStyle: "italic",
                      }}
                    >
                      No entries available.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {/* ================= 16. RFI LOGS ================= */}
            <Box mt={4}>
              <Typography
                sx={{
                  ...TYPOGRAPHY.H3,
                  color: COLORS.PRIMARY,
                  fontFamily: FONT_FAMILY,
                  mb: 2,
                }}
              >
                16. RFI Logs
              </Typography>

              {/* Table - Read Only */}
              <Box mb={1.5}>
                {/* Table Header */}
                <Box
                  display="flex"
                  sx={{
                    borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                    py: 0.8,
                  }}
                >
                  <Box
                    flex={0.8}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                      px: 1,
                    }}
                  >
                    RFI No.
                  </Box>
                  <Box
                    flex={1.5}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                      px: 1,
                    }}
                  >
                    Description
                  </Box>
                  <Box
                    flex={1}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                      px: 1,
                    }}
                  >
                    Issue Date
                  </Box>
                  <Box
                    flex={0.8}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                      px: 1,
                    }}
                  >
                    Status
                  </Box>
                  <Box
                    flex={1}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                      px: 1,
                    }}
                  >
                    Expected Date
                  </Box>
                  <Box
                    flex={1}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                      px: 1,
                    }}
                  >
                    Remark
                  </Box>
                </Box>

                {/* Table Body */}
                {mprData.rfiLogs?.length > 0 ? (
                  mprData.rfiLogs.map((row, index) => (
                    <Box
                      key={index}
                      display="flex"
                      sx={{
                        borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                        py: 1,
                        minHeight: "60px",
                      }}
                    >
                      <Box
                        flex={0.8}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.rfiNo}
                      </Box>
                      <Box
                        flex={1.5}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.description}
                      </Box>
                      <Box
                        flex={1}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.issueDate}
                      </Box>
                      <Box
                        flex={0.8}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color:
                            row.status === "Open"
                              ? COLORS.ERROR
                              : COLORS.SUCCESS,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          fontWeight: "bold",
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.status}
                      </Box>
                      <Box
                        flex={1}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.expectedDate}
                      </Box>
                      <Box
                        flex={1}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.remarks}
                      </Box>
                    </Box>
                  ))
                ) : (
                  <Box
                    display="flex"
                    sx={{
                      borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                      py: 2,
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: FONT_FAMILY,
                        color: COLORS.LIGHT_SECONDARY,
                        fontStyle: "italic",
                      }}
                    >
                      No RFI logs available.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {/* ================= 17. LOOK AHEAD PLAN (NEXT MONTH) ================= */}
            <Box mt={4}>
              <Typography
                sx={{
                  ...TYPOGRAPHY.H3,
                  color: COLORS.PRIMARY,
                  fontFamily: FONT_FAMILY,
                  mb: 2,
                }}
              >
                17. Look Ahead Plan (Next Month)
              </Typography>

              {/* Table - Read Only */}
              <Box mb={1.5}>
                {/* Table Header */}
                <Box
                  display="flex"
                  sx={{
                    borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                    py: 0.8,
                  }}
                >
                  <Box
                    flex={1.5}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                      px: 1,
                    }}
                  >
                    Activity
                  </Box>
                  <Box
                    flex={1}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                      px: 1,
                    }}
                  >
                    Start Date
                  </Box>
                  <Box
                    flex={1}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                      px: 1,
                    }}
                  >
                    End Date
                  </Box>
                  <Box
                    flex={1.5}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                      px: 1,
                    }}
                  >
                    Resource Requirement
                  </Box>
                  <Box
                    flex={1.5}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                      px: 1,
                    }}
                  >
                    Remarks
                  </Box>
                </Box>

                {/* Table Body */}
                {mprData.lookAheadPlan.length > 0 ? (
                  mprData.lookAheadPlan.map((row, index) => (
                    <Box
                      key={index}
                      display="flex"
                      sx={{
                        borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                        py: 1,
                        minHeight: "60px",
                      }}
                    >
                      <Box
                        flex={1.5}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.activity}
                      </Box>
                      <Box
                        flex={1}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.startDate}
                      </Box>
                      <Box
                        flex={1}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.endDate}
                      </Box>
                      <Box
                        flex={1.5}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.resourceRequirement}
                      </Box>
                      <Box
                        flex={1.5}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.remarks}
                      </Box>
                    </Box>
                  ))
                ) : (
                  <Box
                    display="flex"
                    sx={{
                      borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                      py: 2,
                      justifyContent: "center",
                      minHeight: "60px",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: FONT_FAMILY,
                        color: COLORS.LIGHT_SECONDARY,
                        fontStyle: "italic",
                      }}
                    >
                      No entries available.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {/* ================= 18. PHOTOGRAPHIC DOCUMENTATION ================= */}
            <Box mt={4}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Typography
                  sx={{
                    ...TYPOGRAPHY.H3,
                    fontFamily: FONT_FAMILY,
                    color: COLORS.PRIMARY,
                  }}
                >
                  18. Photographic Documentation
                </Typography>
              </Box>

              {/* Photo Grid */}
              {uploadedPhotos.length > 0 ? (
                <Grid container spacing={2} mb={3}>
                  {uploadedPhotos.map((photo, index) => (
                    <Grid item xs={12} sm={6} key={index}>
                      <Box
                        sx={{
                          width: "100%",
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                        }}
                      >
                        {/* Image Container */}
                        <Box
                          sx={{
                            position: "relative",
                            width: "100%",
                            height: "250px",
                            overflow: "hidden",
                            borderRadius: "8px",
                            backgroundColor: "#f5f5f5",
                          }}
                        >
                          <img
                            src={
                              photo.imageUrl ||
                              (photo.file
                                ? URL.createObjectURL(photo.file)
                                : "")
                            }
                            alt={photo.title || photo.name}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        </Box>

                        {/* Data below the image */}
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                            gap: 0.5,
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              fontFamily: FONT_FAMILY,
                              color: COLORS.TEXT,
                              fontWeight: "medium",
                              fontSize: "16px",
                              lineHeight: 1.2,
                            }}
                          >
                            {photo.title || photo.name}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Box
                  sx={{
                    textAlign: "center",
                    py: 8,
                    border: `2px dashed ${COLORS.LIGHT_SECONDARY}`,
                    borderRadius: "12px",
                    backgroundColor: "#fafafa",
                  }}
                >
                  <Icon name="image" size={48} color={COLORS.LIGHT_SECONDARY} />
                  <Typography
                    sx={{
                      ...TYPOGRAPHY.BODY_STANDARD,
                      color: COLORS.LIGHT_SECONDARY,
                      mt: 2,
                      fontFamily: FONT_FAMILY,
                    }}
                  >
                    No photos available
                  </Typography>
                </Box>
              )}
            </Box>
          </>
        )}

        {/* Toast Component */}
        <AppToast
          open={toast.open}
          type={toast.type}
          title={toast.title}
          message={toast.message}
          onClose={closeToast}
        />
      </Box>
    </LocalizationProvider>
  );
}
